import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAutenticacion } from '../context/AuthContext';
import {
  obtenerUsuariosApi, obtenerUsuariosBloqueadosApi, crearUsuarioApi,
  actualizarUsuarioApi, eliminarUsuarioApi, desbloquearUsuarioApi,
} from '../services/api';
import { availableModuleOptions } from '../utils/modulePermissions';

const campoClase = 'mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100';
const botonClase = 'rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:border-emerald-500 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-200';
const botonPrimarioClase = 'rounded-xl bg-[#006d44] px-4 py-2 text-sm font-semibold text-white hover:bg-[#005c39] disabled:cursor-not-allowed disabled:opacity-50';

function formatearFechaHora(valor) {
  if (!valor) return '—';
  const fecha = new Date(valor);
  return Number.isNaN(fecha.getTime()) ? String(valor) : new Intl.DateTimeFormat('es-AR', {
    dateStyle: 'short', timeStyle: 'short',
  }).format(fecha);
}

function alternarPermiso(actuales, clave) {
  if (actuales.includes(clave)) {
    return actuales.filter((item) => item !== clave &&
      (clave !== 'ficha_pacientes' || item !== 'ficha_pacientes_editar'));
  }
  return clave === 'ficha_pacientes_editar'
    ? Array.from(new Set([...actuales, 'ficha_pacientes', clave]))
    : [...actuales, clave];
}

function Ventana({ titulo, children, alCerrar, bloqueada = false }) {
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4">
    <div role="dialog" aria-modal="true" aria-label={titulo}
      className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl dark:bg-slate-800 sm:p-6">
      <div className="mb-5 flex items-start justify-between gap-4">
        <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">{titulo}</h3>
        <button type="button" onClick={alCerrar} disabled={bloqueada} aria-label="Cerrar"
          className="rounded-lg px-2 py-1 text-2xl leading-none text-slate-500 hover:bg-slate-100 disabled:opacity-50 dark:hover:bg-slate-700">×</button>
      </div>
      {children}
    </div>
  </div>;
}

export default function Users() {
  const { usuario: usuarioActual } = useAutenticacion();
  const opciones = availableModuleOptions();
  const [usuarios, setUsuarios] = useState([]);
  const [bloqueados, setBloqueados] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState('todos');
  const [editor, setEditor] = useState(null);
  const [eliminando, setEliminando] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [lista, listaBloqueados] = await Promise.all([
        obtenerUsuariosApi(), obtenerUsuariosBloqueadosApi().catch(() => []),
      ]);
      setUsuarios(lista);
      setBloqueados(listaBloqueados);
    } catch (err) {
      setError(err?.message || 'No se pudieron cargar los usuarios.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const administradores = usuarios.filter((u) => u.isAdmin).length;
  const usuariosVisibles = useMemo(() => {
    const texto = busqueda.trim().toLocaleLowerCase('es');
    return usuarios.filter((u) => {
      if (filtro === 'admins' && !u.isAdmin) return false;
      if (filtro === 'operadores' && u.isAdmin) return false;
      return !texto || u.username.toLocaleLowerCase('es').includes(texto);
    });
  }, [usuarios, busqueda, filtro]);
  const esPropio = (u) => u.username.toLocaleLowerCase('es') ===
    String(usuarioActual?.username || '').toLocaleLowerCase('es');

  const abrirCrear = () => {
    setError(''); setMensaje('');
    setEditor({ modo: 'crear', username: '', password: '', isAdmin: false, modulePermissions: [] });
  };
  const abrirEditar = (u) => {
    setError(''); setMensaje('');
    setEditor({ modo: 'editar', id: u.id, username: u.username, password: '',
      isAdmin: u.isAdmin, modulePermissions: [...(u.modulePermissions || [])] });
  };

  const guardar = async (evento) => {
    evento.preventDefault();
    if (!editor || ocupado) return;
    setOcupado(true); setError(''); setMensaje('');
    try {
      if (editor.modo === 'crear') {
        const nuevo = await crearUsuarioApi(editor);
        setUsuarios((actuales) => [...actuales, nuevo].sort((a, b) => a.username.localeCompare(b.username, 'es')));
        setMensaje(`Usuario ${nuevo.username} creado.`);
      } else {
        const cambios = { isAdmin: editor.isAdmin };
        if (!editor.isAdmin) cambios.modulePermissions = editor.modulePermissions;
        if (editor.password) cambios.password = editor.password;
        const actualizado = await actualizarUsuarioApi(editor.id, cambios);
        setUsuarios((actuales) => actuales.map((u) => u.id === actualizado.id ? actualizado : u));
        setMensaje(`Permisos de ${actualizado.username} guardados.`);
      }
      setEditor(null);
    } catch (err) {
      setError(err?.message || 'No se pudieron guardar los cambios.');
    } finally {
      setOcupado(false);
    }
  };

  const confirmarEliminacion = async () => {
    if (!eliminando || ocupado) return;
    setOcupado(true); setError(''); setMensaje('');
    try {
      await eliminarUsuarioApi(eliminando.id);
      setUsuarios((actuales) => actuales.filter((u) => u.id !== eliminando.id));
      setBloqueados((actuales) => actuales.filter((u) => u.username !== eliminando.username));
      setMensaje(`Usuario ${eliminando.username} eliminado.`);
      setEliminando(null);
    } catch (err) {
      setError(err?.message || 'No se pudo eliminar el usuario.');
    } finally {
      setOcupado(false);
    }
  };

  const desbloquear = async (username) => {
    if (ocupado) return;
    setOcupado(true); setError(''); setMensaje('');
    try {
      await desbloquearUsuarioApi(username);
      setBloqueados((actuales) => actuales.filter((u) => u.username !== username));
      setMensaje(`Usuario ${username} desbloqueado.`);
    } catch (err) {
      setError(err?.message || 'No se pudo desbloquear el usuario.');
    } finally {
      setOcupado(false);
    }
  };

  const grupos = [
    { titulo: 'Módulos', opciones: opciones.filter((o) => !o.key.startsWith('ficha_pacientes')) },
    { titulo: 'Ficha de pacientes', opciones: opciones.filter((o) => o.key.startsWith('ficha_pacientes')) },
  ];
  const editorPropio = editor?.modo === 'editar' && esPropio(editor);
  const ultimoAdmin = editor?.modo === 'editar' && editor.isAdmin && administradores <= 1;

  return <section className="mx-auto max-w-6xl space-y-5 px-4 py-6 sm:px-6 lg:px-8">
    <header className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 pb-5 dark:border-slate-700">
      <div>
        <h2 className="text-2xl font-extrabold text-slate-800 dark:text-slate-100">Usuarios</h2>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">Administrá cuentas, roles y permisos desde un solo lugar.</p>
      </div>
      <button type="button" className={botonPrimarioClase} onClick={abrirCrear}>Nuevo usuario</button>
    </header>

    {mensaje && <p role="status" className="rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">{mensaje}</p>}
    {error && !editor && !eliminando && <p role="alert" className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-200">{error}</p>}

    <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <div className="flex flex-wrap items-end gap-3">
        <label className="min-w-[230px] flex-1 text-sm font-semibold text-slate-700 dark:text-slate-200">
          Buscar usuario
          <input className={campoClase} type="search" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Nombre de usuario" />
        </label>
        <label className="text-sm font-semibold text-slate-700 dark:text-slate-200">
          Mostrar
          <select className={campoClase} value={filtro} onChange={(e) => setFiltro(e.target.value)}>
            <option value="todos">Todos</option><option value="admins">Administradores</option><option value="operadores">Operadores</option>
          </select>
        </label>
        <span className="pb-2 text-sm text-slate-500 dark:text-slate-300">{usuariosVisibles.length} de {usuarios.length}</span>
      </div>
    </div>

    {cargando ? <p role="status" className="py-6 text-sm text-slate-500">Cargando usuarios...</p> :
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
        {usuariosVisibles.length ? <ul className="divide-y divide-slate-100 dark:divide-slate-700">
          {usuariosVisibles.map((u) => <li key={u.id} className="flex flex-wrap items-center justify-between gap-4 p-4 sm:px-5">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <strong className="text-sm text-slate-900 dark:text-slate-100">{u.username}</strong>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${u.isAdmin ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200' : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200'}`}>
                  {u.isAdmin ? 'Administrador' : 'Operador'}
                </span>
                {esPropio(u) && <span className="text-xs text-slate-500">Tu cuenta</span>}
              </div>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-300">
                {u.isAdmin ? 'Todos los permisos' : opciones.filter((o) => u.modulePermissions?.includes(o.key)).map((o) => o.label).join(' · ') || 'Sin permisos'}
              </p>
              <p className="mt-1 text-xs text-slate-400">Creado: {formatearFechaHora(u.createdAt)}</p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button type="button" className={botonClase} onClick={() => abrirEditar(u)}>Editar</button>
              <button type="button" className={`${botonClase} border-rose-200 text-rose-700 hover:border-rose-500 hover:text-rose-800 dark:border-rose-800 dark:text-rose-300`}
                onClick={() => { setError(''); setMensaje(''); setEliminando(u); }} disabled={esPropio(u) || (u.isAdmin && administradores <= 1)}
                title={esPropio(u) ? 'No podés eliminar tu propia cuenta' : u.isAdmin && administradores <= 1 ? 'Debe quedar al menos un administrador' : ''}>
                Eliminar
              </button>
            </div>
          </li>)}
        </ul> : <p className="p-6 text-sm text-slate-500">No hay usuarios que coincidan con la búsqueda.</p>}
      </div>}

    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-4 dark:border-slate-700">
        <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">Usuarios bloqueados</h3>
        <span className="text-sm text-slate-500">{bloqueados.length}</span>
      </div>
      {bloqueados.length ? <ul className="divide-y divide-slate-100 dark:divide-slate-700">{bloqueados.map((u) =>
        <li key={u.username} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm">
          <div><strong>{u.username}</strong><p className="text-xs text-slate-500">{u.failedCount} intentos · Hasta {formatearFechaHora(u.blockedUntil)}</p></div>
          <button type="button" className={botonClase} disabled={ocupado} onClick={() => desbloquear(u.username)}>Desbloquear</button>
        </li>)}</ul> : <p className="px-5 py-4 text-sm text-slate-500">No hay usuarios bloqueados.</p>}
    </div>

    {editor && <Ventana titulo={editor.modo === 'crear' ? 'Nuevo usuario' : `Editar ${editor.username}`}
      alCerrar={() => setEditor(null)} bloqueada={ocupado}>
      <form onSubmit={guardar} className="space-y-5">
        {editor.modo === 'crear' ? <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200">
          Nombre de usuario
          <input className={campoClase} required autoFocus autoComplete="off" value={editor.username}
            onChange={(e) => setEditor((actual) => ({ ...actual, username: e.target.value }))} />
        </label> : <p className="text-sm text-slate-500">Modificá el rol, los permisos o la contraseña de esta cuenta.</p>}

        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200">
          {editor.modo === 'crear' ? 'Contraseña' : 'Nueva contraseña (opcional)'}
          <input className={campoClase} type="password" minLength={8} required={editor.modo === 'crear'}
            autoComplete="new-password" value={editor.password} placeholder={editor.modo === 'crear' ? 'Mínimo 8 caracteres' : 'Dejar vacío para conservarla'}
            onChange={(e) => setEditor((actual) => ({ ...actual, password: e.target.value }))} />
        </label>

        <label className="flex items-start gap-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-800 dark:bg-slate-700 dark:text-slate-100">
          <input type="checkbox" className="mt-0.5 accent-[#006d44]" checked={editor.isAdmin}
            disabled={editorPropio || ultimoAdmin}
            onChange={(e) => setEditor((actual) => ({ ...actual, isAdmin: e.target.checked }))} />
          <span><strong>Administrador</strong><span className="block text-xs text-slate-500 dark:text-slate-300">Acceso completo, incluida la gestión de usuarios.</span></span>
        </label>
        {(editorPropio || ultimoAdmin) && <p className="text-xs text-slate-500">No se puede quitar el acceso de administrador a tu cuenta o al último administrador.</p>}

        {!editor.isAdmin && grupos.map((grupo) => <fieldset key={grupo.titulo} className="rounded-xl border border-slate-200 p-4 dark:border-slate-600">
          <legend className="px-1 text-sm font-semibold text-slate-700 dark:text-slate-200">{grupo.titulo}</legend>
          <div className="grid gap-2 sm:grid-cols-2">{grupo.opciones.map((opcion) =>
            <label key={opcion.key} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-700">
              <input type="checkbox" className="accent-[#006d44]" checked={editor.modulePermissions.includes(opcion.key)}
                onChange={() => setEditor((actual) => ({ ...actual, modulePermissions: alternarPermiso(actual.modulePermissions, opcion.key) }))} />
              {opcion.label}
            </label>)}</div>
        </fieldset>)}
        {!editor.isAdmin && <p className="text-xs text-slate-500 dark:text-slate-300">Editar ficha también habilita verla, dar de baja, dar de alta y autorizar pacientes. Eliminar pacientes requiere ser administrador.</p>}
        {error && <p role="alert" className="rounded-xl bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-200">{error}</p>}
        <div className="flex justify-end gap-2 border-t border-slate-100 pt-4 dark:border-slate-700">
          <button type="button" className={botonClase} disabled={ocupado} onClick={() => setEditor(null)}>Cancelar</button>
          <button type="submit" className={botonPrimarioClase} disabled={ocupado || (!editor.isAdmin && !editor.modulePermissions.length)}>
            {ocupado ? 'Guardando...' : editor.modo === 'crear' ? 'Crear usuario' : 'Guardar cambios'}
          </button>
        </div>
      </form>
    </Ventana>}

    {eliminando && <Ventana titulo="Eliminar usuario" alCerrar={() => setEliminando(null)} bloqueada={ocupado}>
      <p className="text-sm text-slate-700 dark:text-slate-200">¿Querés eliminar la cuenta <strong>{eliminando.username}</strong>? Perderá el acceso de inmediato.</p>
      {error && <p role="alert" className="mt-4 rounded-xl bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-200">{error}</p>}
      <div className="mt-6 flex justify-end gap-2">
        <button type="button" className={botonClase} disabled={ocupado} onClick={() => setEliminando(null)}>Cancelar</button>
        <button type="button" className="rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
          disabled={ocupado} onClick={confirmarEliminacion}>{ocupado ? 'Eliminando...' : 'Eliminar usuario'}</button>
      </div>
    </Ventana>}
  </section>;
}
